import { graphql, readInlineData } from 'react-relay';
import { trackEvent, trackEcommerce, trackingConstants, eventNameConstants } from 'dibs-tracking';

import { similarAuctionTracking_item$key } from './__generated__/similarAuctionTracking_item.graphql';

const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;
const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST } = eventNameConstants;

export const trackArrowClick = (label: 'left' | 'right', trackingCategory: string): void => {
    trackEvent({
        category: 'promo interaction',
        action: `${trackingCategory} arrow clicked`,
        label,
        isInteractiveEvent: true,
        value: 0,
    });
};

type TrackItemClickArgs = {
    index: number;
    item: similarAuctionTracking_item$key;
    trackingCategory: string;
};

const itemFragment = graphql`
    fragment similarAuctionTracking_item on Item @inline {
        serviceId
        browseUrl
        categoryCode
        categoryPath
        title
        ecommerceTrackingParams
        contemporaryTrackingString
        seller {
            serviceId
        }
        relevantAuctionLot(page: null) {
            buyItNowPrice {
                amount
            }
        }
        relevantAuctionSolution(page: null) {
            bids {
                totalResults
            }
            highestBidAmount {
                amount
            }
        }
    }
`;

export const trackItemClick = ({
    index,
    item: itemRef,
    trackingCategory,
}: TrackItemClickArgs): void => {
    const item = readInlineData(itemFragment, itemRef);
    const { browseUrl, categoryCode, ecommerceTrackingParams, title, serviceId } = item;

    trackEcommerce({
        type: ECOM_PRODUCT_CLICK,
        eventName: EVENT_SELECT_ITEM,
        products: [
            {
                position: index + 1,
                category: browseUrl || '',
                categoryCode: categoryCode || '',
                ...(ecommerceTrackingParams || {}),
                name: title,
            },
        ],
        actionField: {
            list: trackingCategory,
            itemId: serviceId,
        },
    });
};

type TrackItemImpressionArgs = {
    index: number;
    item: similarAuctionTracking_item$key;
    trackingCategory: string;
};

export const trackItemImpression = ({
    item: itemRef,
    index,
    trackingCategory,
}: TrackItemImpressionArgs): void => {
    const item = readInlineData(itemFragment, itemRef);
    const {
        relevantAuctionLot,
        categoryCode,
        categoryPath,
        contemporaryTrackingString,
        title,
        serviceId,
        seller,
        relevantAuctionSolution,
    } = item;
    const { bids, highestBidAmount } = relevantAuctionSolution || {};
    const hasBids = !!bids?.totalResults;

    const price = hasBids ? highestBidAmount?.amount : relevantAuctionLot?.buyItNowPrice?.amount;

    trackEcommerce({
        type: ECOM_PRODUCT_IMPRESSION,
        eventName: EVENT_VIEW_ITEM_LIST,
        actionField: {
            itemId: serviceId,
        },
        category: trackingCategory,
        products: [
            {
                brand: seller?.serviceId || '',
                category: categoryPath,
                categoryCode,
                id: serviceId,
                variant: 'products',
                name: title,
                list: trackingCategory,
                dimension76: 'auction price',
                dimension83: contemporaryTrackingString,
                position: index + 1,
                price,
            },
        ],
    });
};
